import { z } from "zod";

/**
 * The single reader of import.meta.env. Validates at module load so a
 * misconfigured build fails at boot, loudly, instead of at first request with
 * an undefined baseURL. ESLint bans import.meta.env everywhere else.
 *
 * Includes only services used by Investigator. Support, station activity,
 * and template administration use the shared RMS API.
 */
const EnvSchema = z.object({
  VITE_WORKOS_CLIENT_ID: z.string().min(1).optional(),
  // Tolerates empty-string injection like the old `|| "api.workos.com"` did.
  VITE_WORKOS_API_HOSTNAME: z
    .string()
    .optional()
    .transform((v) => (v && v.trim() ? v.trim() : "api.workos.com")),
  VITE_WORKOS_DEV_MODE: z
    .string()
    .optional()
    .transform((v) => v === "true"),

  /**
   * REQUIRED here, unlike in ReportGen where it was optional because most of
   * that app worked without an incidents API. This app is nothing but incident
   * management, so booting without it could only produce requests against the
   * frontend origin.
   */
  VITE_INCIDENTS_API_BASE_URL: z.url(),

  /**
   * Origin of the ReportGen SPA, for the sidebar's cross-app link. Optional:
   * a build without it hides the entry rather than offering a dead link, which
   * is the same shape as ReportGen's own gate on VITE_INVESTIGATOR_APP_URL.
   */
  VITE_REPORTGEN_APP_URL: z.url().optional(),

  VITE_AGENT_API_BASE_URL: z.url(),
  VITE_UPLOAD_API_BASE_URL: z.url(),
  VITE_PROFILE_API_DOMAIN: z.url(),
  // Evidence locker reads (catalog listing + thumbnails) still come from the
  // bodycam/evidence service -- see features/evidence/api/evidenceQueries.ts.
  VITE_BODYCAM_API_DOMAIN: z.url(),
  VITE_RMS_API_BASE_URL: z.url(),

  // Amazon Location Service (Places and Maps) key + region for address
  // autocomplete, geocoding and map tiles. A scoped, referer-restricted public
  // key (`v1.public.…`) provisioned per stage by apps/cdk-web. Optional so the
  // app still boots before it is provisioned -- callers degrade to manual
  // address entry when absent.
  VITE_LOCATION_API_KEY: z
    .string()
    .optional()
    .transform((v) => {
      const trimmed = v?.trim();
      return trimmed ? trimmed : undefined;
    }),
  // Default MUST match LOCATION_SERVICE_REGION in apps/cdk-web (the region the
  // key is provisioned in) -- otherwise requests hit a region the key is not
  // scoped to and fail.
  VITE_LOCATION_REGION: z
    .string()
    .optional()
    .transform((v) => (v && v.trim() ? v.trim() : "us-west-2")),
});

const parsed = EnvSchema.safeParse(import.meta.env);
if (!parsed.success) {
  console.error(
    "[env] invalid VITE_ configuration:",
    z.treeifyError(parsed.error),
  );
  // This throw happens at module scope, before React mounts -- no error
  // boundary can catch it. Paint a readable failure into #root instead of
  // shipping a blank page on deploy misconfiguration.
  const root = document.getElementById("root");
  if (root) {
    root.innerHTML =
      '<div style="padding:32px;font-family:system-ui,sans-serif;text-align:center">' +
      "<h2>Configuration error</h2>" +
      "<p>This deployment is missing or has invalid environment configuration. " +
      "See the browser console for details.</p></div>";
  }
  throw new Error("Invalid environment configuration; see console for details.");
}

export const env = Object.freeze({
  workos: {
    /** May be undefined in local demo-mode builds; the pipeline injects it in CI. */
    clientId: parsed.data.VITE_WORKOS_CLIENT_ID,
    apiHostname: parsed.data.VITE_WORKOS_API_HOSTNAME,
    devMode: parsed.data.VITE_WORKOS_DEV_MODE,
  },
  api: {
    incidents: parsed.data.VITE_INCIDENTS_API_BASE_URL,
    agent: parsed.data.VITE_AGENT_API_BASE_URL,
    upload: parsed.data.VITE_UPLOAD_API_BASE_URL,
    profile: parsed.data.VITE_PROFILE_API_DOMAIN,
    bodycam: parsed.data.VITE_BODYCAM_API_DOMAIN,
    rms: parsed.data.VITE_RMS_API_BASE_URL,
  },
  apps: {
    /** Undefined when this build has no ReportGen origin configured. */
    reportGen: parsed.data.VITE_REPORTGEN_APP_URL,
  },
  location: {
    /** Undefined until the Amazon Location API key is provisioned for this stage. */
    apiKey: parsed.data.VITE_LOCATION_API_KEY,
    region: parsed.data.VITE_LOCATION_REGION,
  },
} as const);

/**
 * Retained for the feature-flag gate in pages/IncidentManagement/featureFlag.ts.
 * Now always true once the schema validates, because
 * VITE_INCIDENTS_API_BASE_URL is required in this app -- kept so the flag's
 * "infra precondition AND per-station toggle" shape stays explicit and the
 * per-station WorkOS gate remains the single thing that varies.
 */
export const incidentsApiConfigured = Boolean(env.api.incidents);
